import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/zustand/auth-store';
import { useUserType } from '@/zustand/user-type-store';
import { USER_TYPES } from '@/constants/constants';
import { useGetHomePatient } from '@/apis/homePatient/query';
import { mapHomePatientResponse } from '@/pages/home/mapHomePatientResponse';
import { trackEvent, setTrackingContext } from '@/services/analytics';
import { takePendingPerformanceTrigger } from '@/utils/performance-trigger';

/** Prescription properties shared by the patient events (from mapHomePatientResponse). */
export const getPrescriptionTrackingProps = mapped => ({
  week_no: mapped?.prescriptionWeek,
  prescription_duration: mapped?.prescriptionDuration,
  frequency_a_day: mapped?.progressTracker?.frequency?.day,
  frequency_a_week: mapped?.progressTracker?.frequency?.week,
});

/**
 * App-level patient events, from the GET /sessions/current data:
 * - RETENTION_SIGNAL (PATIENT_RETAINED) once per sign-in - not on the onboarding screens;
 * - PERFORMANCE_TRIGGER_CLICKED (PATIENT_CLICKED_PERFORMANCE_TRIGGER) for a session-reminder
 *   tap, wherever the patient lands (HEP Viewer or OB1), with the link's trigger_type and
 *   schedule_type.
 */
export function useRetentionSignal(identityReady) {
  const { accessToken, user } = useAuth();
  const userType = useUserType(state => state.userType);
  const isPatient = userType === USER_TYPES.PATIENT;
  const firedRef = useRef(false);
  const triggerClickFiredRef = useRef(false);
  // Onboarding screens must not count as a retention signal - it fires once the patient lands in the app.
  const isOnOnboarding = useLocation().pathname.startsWith('/patients/onboarding');

  const homeQuery = useGetHomePatient({ enabled: !!accessToken && isPatient });
  const homePatientData = homeQuery.data;
  const isHomeSettled = homeQuery.isSuccess || homeQuery.isError;

  useEffect(() => {
    if (!accessToken) {
      firedRef.current = false;
      return;
    }
    if (!identityReady || !isPatient || isOnOnboarding || !homePatientData || firedRef.current) {
      return;
    }
    const mapped = mapHomePatientResponse(homePatientData);
    if (!mapped) {
      return;
    }

    const therapist = mapped.prescription?.therapist ?? null;
    const therapistName = therapist
      ? therapist.name ||
        [therapist.firstName ?? therapist.first_name, therapist.lastName ?? therapist.last_name]
          .filter(Boolean)
          .join(' ') ||
        null
      : null;
    const patientName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

    setTrackingContext({
      patient: user?.id,
      therapist: therapist?.id,
      prescription: mapped.prescription.id,
    });

    firedRef.current = true;
    if (mapped.progressTracker?.prescriptionStatus !== 'COMPLETED') {
      trackEvent('RETENTION_SIGNAL', getPrescriptionTrackingProps(mapped));
    }
  }, [accessToken, identityReady, isPatient, isOnOnboarding, homePatientData, user]);

  // Reminder tap: tracked even without a prescription, and even when it lands on onboarding.
  useEffect(() => {
    if (!accessToken) {
      triggerClickFiredRef.current = false;
      return;
    }
    if (!identityReady || !isPatient || !isHomeSettled || triggerClickFiredRef.current) {
      return;
    }
    triggerClickFiredRef.current = true;
    const click = takePendingPerformanceTrigger();
    if (!click) {
      return;
    }
    const mapped = homePatientData ? mapHomePatientResponse(homePatientData) : null;
    trackEvent('PERFORMANCE_TRIGGER_CLICKED', {
      ...getPrescriptionTrackingProps(mapped),
      trigger_type: click.trigger_type,
      schedule_type: click.schedule_type,
    });
  }, [accessToken, identityReady, isPatient, isHomeSettled, homePatientData]);
}
